import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from '../exportUtils';
import { accountingApi } from '../../../services/accountingApi';
import apiService from '../../../services/api';
import ViewSaleModal from '../ViewSaleModal';
import ViewPurchaseModal from '../ViewPurchaseModal';

const SECTION_TABS = ['sell', 'sell_payment', 'purchase', 'purchase_payment', 'expense', 'All'];
const TAB_LABELS   = { sell: 'Sell', sell_payment: 'Sales Payments', purchase: 'Purchases', purchase_payment: 'Purchase Payments', expense: 'Expenses', All: 'All' };

/* ─── Status Badge ─────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const styles = {
    Paid:    { background: '#dcfce7', color: '#166534' },
    Due:     { background: '#fee2e2', color: '#991b1b' },
    Partial: { background: '#fef9c3', color: '#854d0e' },
    Overdue: { background: '#ffe4e6', color: '#9f1239' },
  };
  const s = styles[status] || { background: '#f1f5f9', color: '#475569' };
  return (
    <span style={{ ...s, padding: '0.25rem 0.65rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
      {status}
    </span>
  );
};

/* ─── Main Component ───────────────────────────────────────── */
const Transactions = () => {
  const [activeSection, setActiveSection]   = useState('sell');
  const [allData, setAllData]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState('');
  const [search, setSearch]                 = useState('');
  const [entries, setEntries]               = useState(25);
  const [showExport, setShowExport]         = useState(false);
  const [showFilter, setShowFilter]         = useState(false);
  const [filterStatus, setFilterStatus]     = useState('All');
  const [filterMethod, setFilterMethod]     = useState('All');
  const [selectedSaleForView, setSelectedSaleForView]         = useState(null);
  const [selectedPurchaseForView, setSelectedPurchaseForView] = useState(null);
  const [selectedExpenseForView, setSelectedExpenseForView]   = useState(null);
  const [currentPage, setCurrentPage]       = useState(1);
  const exportRef  = useRef(null);
  const [mapModal, setMapModal] = useState(null); // { row, accounts, depositTo, paymentAccount, description, saving }

  const handleViewSale = async (r) => {
    if (!r) return;
    const typeStr = (r.sub_type || r.type || '').toLowerCase();
    const targetId = r.transaction_id || r.id;

    if (typeStr.includes('expense')) {
      const initialExpense = {
        ...r,
        ref_no: r.ref_no || r.invoice_no,
        transaction_date: r.transaction_date || r.date,
        status: r.status || 'final',
        payment_status: r.payment_status || r.paymentStatus || 'Paid',
        location_name: r.location_name || r.location?.name || '',
        contact_name: r.contact_name || r.contact?.name || '—',
        additional_notes: r.additional_notes || r.note || '',
        staff_note: r.staff_note || '',
      };
      setSelectedExpenseForView(initialExpense);
    } else if (typeStr.includes('purchase')) {
      const initialPurchase = {
        ...r,
        ref_no: r.ref_no || r.invoice_no,
        transaction_date: r.transaction_date || r.date,
        status: r.status || 'received',
        payment_status: r.payment_status || r.paymentStatus || 'Paid',
        location: { name: r.location_name },
        contact: { name: r.contact_name, mobile: r.mobile },
        purchase_lines: [],
        payment_lines: []
      };
      setSelectedPurchaseForView(initialPurchase);

      try {
        const response = await apiService.getPurchase(targetId);
        let fullPurchase = response?.data || response;
        if (Array.isArray(fullPurchase)) fullPurchase = fullPurchase[0];
        if (fullPurchase) {
          setSelectedPurchaseForView(fullPurchase);
        }
      } catch (err) {
        /* Keep initial purchase data on error */
      }
    } else {
      const initialSale = {
        ...r,
        invoiceNo: r.invoice_no || r.ref_no,
        saleDate: r.transaction_date || r.date,
        status: r.status || 'final',
        paymentStatus: r.payment_status || r.paymentStatus,
        customer: r.contact_name,
        locationId: r.location_id,
        products: [],
        apiData: {
          invoice_no: r.invoice_no || r.ref_no,
          transaction_date: r.transaction_date || r.date,
          status: r.status || 'final',
          payment_status: r.payment_status || r.paymentStatus,
          contact: { name: r.contact_name, mobile: r.mobile },
          final_total: r.final_total,
          total_paid: r.total_paid,
          sell_lines: []
        }
      };
      setSelectedSaleForView(initialSale);

      try {
        const response = await apiService.getSale(targetId);
        let fullSale = response?.data?.transaction || response?.data?.[0] || response?.data || response;
        if (fullSale) {
          setSelectedSaleForView({ ...initialSale, apiData: fullSale });
        }
      } catch (err) {
        /* Keep initial sale data on error */
      }
    }
  };

  const openMapModal = async (r) => {
    try {
      const res = await accountingApi.getMapDetails(r.id, r.sub_type || r.type || 'sell');
      const accounts = res.data?.accounts || [];
      setMapModal({
        row: r,
        accounts,
        depositTo: res.data?.default_deposit_to || (accounts[0]?.id || ''),
        paymentAccount: res.data?.default_payment_account || (accounts[0]?.id || ''),
        description: res.data?.note || '',
        saving: false,
      });
    } catch (e) {
      toast.error(`Failed to load mapping details: ${e.message}`);
    }
  };

  const handleSaveMap = async () => {
    if (!mapModal) return;
    setMapModal(m => ({ ...m, saving: true }));
    try {
      await accountingApi.saveMapTransaction({
        type: mapModal.row.sub_type || mapModal.row.type || 'sell',
        id: mapModal.row.id,
        deposit_to: mapModal.depositTo,
        payment_account: mapModal.paymentAccount,
        description: mapModal.description,
      });
      toast.success('Transaction mapped successfully!');
      setMapModal(null);
    } catch (e) {
      toast.error(`Mapping failed: ${e.message}`);
      setMapModal(m => ({ ...m, saving: false }));
    }
  };

  useEffect(() => {
    setLoading(true);
    const secParam = activeSection === 'All' ? '' : activeSection;
    accountingApi.getTransactions(secParam)
      .then(res => {
        const arr = Array.isArray(res) ? res
          : Array.isArray(res.data) ? res.data
          : Array.isArray(res.data?.data) ? res.data.data
          : [];
        setAllData(arr);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [activeSection]);

  /* Reset pagination on filter or entries change */
  useEffect(() => {
    setCurrentPage(1);
  }, [activeSection, search, filterStatus, filterMethod, entries]);

  /* Close export dropdown on outside click */
  useEffect(() => {
    const handler = (e) => { if (exportRef.current && !exportRef.current.contains(e.target)) setShowExport(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* Helper for truncated pagination numbers with dots */
  const getPaginationRange = (current, total) => {
    const delta = 1;
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
        range.push(i);
      }
    }

    for (let i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i;
    }
    return rangeWithDots;
  };

  /* Filtered data */
  const sectionData = activeSection === 'All' ? allData : allData.filter(r => (r.sub_type || r.type || r.transaction_type || '').toLowerCase() === activeSection.toLowerCase());
  const allFiltered = sectionData.filter(r => {
    const term = search.toLowerCase();
    const matchSearch = (r.contact_name || r.customer || '').toLowerCase().includes(term) ||
                        (r.invoice_no || r.ref_no || '').toLowerCase().includes(term);
    const status = r.payment_status || r.paymentStatus || '';
    const method = r.payment_method || r.paymentMethod || '';
    const matchStatus = filterStatus === 'All' || status === filterStatus;
    const matchMethod = filterMethod === 'All' || method === filterMethod;
    return matchSearch && matchStatus && matchMethod;
  });

  const totalPages = Math.ceil(allFiltered.length / entries) || 1;
  const paginatedData = allFiltered.slice((currentPage - 1) * entries, currentPage * entries);

  const startEntry = allFiltered.length === 0 ? 0 : (currentPage - 1) * entries + 1;
  const endEntry = Math.min(currentPage * entries, allFiltered.length);

  /* Export handler */
  const handleExport = (format) => {
    if (!allFiltered.length) { toast.warning('No data to export'); setShowExport(false); return; }
    const headers = ['Invoice No.', 'Date', 'Contact', 'Payment Status', 'Payment Method', 'Total Amount', 'Total Paid'];
    const rows = allFiltered.map(r => [
      r.invoice_no || r.ref_no || '—',
      r.transaction_date || r.date || '—',
      r.contact_name || r.customer || '—',
      r.payment_status || r.paymentStatus || '—',
      r.payment_method || r.paymentMethod || '—',
      (parseFloat(r.final_total || r.totalAmount) || 0).toFixed(2),
      (parseFloat(r.total_paid || r.totalPaid) || 0).toFixed(2),
    ]);
    const title    = `Transactions — ${TAB_LABELS[activeSection] || activeSection}`;
    const filename = `transactions_${activeSection}`;
    if (format === 'CSV')   exportToCSV(headers, rows, filename);
    if (format === 'Excel') exportToExcel(headers, rows, filename);
    if (format === 'PDF')   exportToPDF(title, headers, rows, filename);
    if (format === 'Print') printPage();
    setShowExport(false);
  };

  const resetFilters = () => { setFilterStatus('All'); setFilterMethod('All'); };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading transactions...</div>;
  if (error)   return <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>Error: {error}</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

      {/* ── Section Tabs Pill Bar ── */}
      <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '0.25rem' }}>
        {SECTION_TABS.map(tab => (
          <button
            key={tab}
            onClick={() => { setActiveSection(tab); setSearch(''); setFilterStatus('All'); setFilterMethod('All'); }}
            style={{
              padding: '0.6rem 1.25rem',
              borderRadius: '999px',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              border: 'none',
              background: activeSection === tab ? 'var(--acc-primary)' : 'var(--acc-surface)',
              color: activeSection === tab ? '#fff' : 'var(--acc-text-main)',
              boxShadow: activeSection === tab ? '0 4px 12px rgba(99, 102, 241, 0.3)' : 'var(--acc-shadow-sm)',
              transition: 'all 0.2s ease'
            }}
          >
            {TAB_LABELS[tab] || tab}
          </button>
        ))}
      </div>

      {/* ── Card wrapper ── */}
      <div className="table-content-card" style={{ padding: 0, overflow: 'hidden' }}>

        {/* ── Toolbar ── */}
        <div className="table-controls" style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--acc-border)', background: 'var(--acc-surface)' }}>
          {/* Left: entries */}
          <div className="table-controls-left" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--acc-text-muted)' }}>Show</span>
            <select
              value={entries}
              onChange={e => setEntries(Number(e.target.value))}
              style={{ height: '40px', padding: '0 0.75rem', border: '1px solid var(--acc-border)', borderRadius: 'var(--acc-radius-sm)', fontSize: '0.875rem', outline: 'none', background: 'var(--acc-surface)' }}
            >
              {[10, 25, 50, 100].map(n => <option key={n}>{n}</option>)}
            </select>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--acc-text-muted)' }}>entries</span>
          </div>

          {/* Right: search + filter + export */}
          <div className="table-controls-right">
            {/* Search */}
            <div className="tabs-search-wrapper">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--acc-text-muted)', marginLeft: '0.5rem' }}>
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                className="tabs-search-input"
                placeholder="Search transactions..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>

            {/* Filter button */}
            <button
              className="acc-btn-secondary"
              onClick={() => setShowFilter(true)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.8 }}><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/></svg>
              Filters
            </button>

            {/* Export dropdown */}
            <div ref={exportRef} style={{ position: 'relative' }}>
              <button
                className="acc-btn-secondary"
                onClick={() => setShowExport(p => !p)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.8 }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export ▾
              </button>
              {showExport && (
                <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 0.5rem)', background: 'var(--acc-surface)', border: '1px solid var(--acc-border)', borderRadius: 'var(--acc-radius)', boxShadow: 'var(--acc-shadow-lg)', zIndex: 50, minWidth: '160px', overflow: 'hidden' }}>
                  {['CSV','Excel','PDF','Print'].map(f => (
                    <div key={f} onClick={() => handleExport(f)}
                      style={{ padding: '0.75rem 1rem', cursor: 'pointer', fontSize: '0.9rem', color: 'var(--acc-text-main)', borderBottom: f !== 'Print' ? '1px solid var(--acc-border)' : 'none', transition: 'background 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--acc-bg)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      Export {f}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Active filter badges ── */}
        {(filterStatus !== 'All' || filterMethod !== 'All') && (
          <div style={{ padding: '0.75rem 1.5rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', borderBottom: '1px solid var(--acc-border)', background: 'var(--acc-bg)', alignItems: 'center' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--acc-text-muted)' }}>Active Filters:</span>
            {filterStatus !== 'All' && (
              <span className="acc-badge acc-badge-blue">
                Status: {filterStatus} <span style={{ cursor: 'pointer', marginLeft: '6px', fontSize: '1rem', lineHeight: 1 }} onClick={() => setFilterStatus('All')}>×</span>
              </span>
            )}
            {filterMethod !== 'All' && (
              <span className="acc-badge acc-badge-blue">
                Method: {filterMethod} <span style={{ cursor: 'pointer', marginLeft: '6px', fontSize: '1rem', lineHeight: 1 }} onClick={() => setFilterMethod('All')}>×</span>
              </span>
            )}
            <button onClick={resetFilters} style={{ background: 'none', border: 'none', color: 'var(--acc-danger)', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, transition: 'opacity 0.2s' }} onMouseEnter={e => e.target.style.opacity = 0.8} onMouseLeave={e => e.target.style.opacity = 1}>Clear All</button>
          </div>
        )}

        {/* ── Table ── */}
        {/* ── Table ── */}
        <div className="acc-table-wrapper" style={{ overflowX: 'auto', width: '100%' }}>
          <table className="acc-table" style={{ width: '100%', minWidth: '1550px', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '150px' }} />
              <col style={{ width: '135px' }} />
              <col style={{ width: '115px' }} />
              <col style={{ width: '140px' }} />
              <col style={{ width: '120px' }} />
              <col style={{ width: '115px' }} />
              <col style={{ width: '110px' }} />
              <col style={{ width: '120px' }} />
              <col style={{ width: '105px' }} />
              <col style={{ width: '105px' }} />
              <col style={{ width: '115px' }} />
              <col style={{ width: '110px' }} />
              <col style={{ width: '110px' }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ width: '150px', padding: '0.6rem 0.5rem', verticalAlign: 'bottom', textAlign: 'center' }}>Action</th>
                <th style={{ width: '135px', padding: '0.6rem 0.5rem', verticalAlign: 'bottom', textAlign: 'center' }}>Date</th>
                <th style={{ width: '115px', padding: '0.6rem 0.5rem', verticalAlign: 'bottom', textAlign: 'left' }}>Invoice No.</th>
                <th style={{ width: '140px', padding: '0.6rem 0.5rem', verticalAlign: 'bottom', lineHeight: '1.2', textAlign: 'left' }}>Customer<br />name</th>
                <th style={{ width: '120px', padding: '0.6rem 0.5rem', verticalAlign: 'bottom', lineHeight: '1.2', textAlign: 'center' }}>Contact<br />Number</th>
                <th style={{ width: '115px', padding: '0.6rem 0.5rem', verticalAlign: 'bottom', textAlign: 'center' }}>Location</th>
                <th style={{ width: '110px', padding: '0.6rem 0.5rem', verticalAlign: 'bottom', lineHeight: '1.2', textAlign: 'center' }}>Payment<br />Status</th>
                <th style={{ width: '120px', padding: '0.6rem 0.5rem', verticalAlign: 'bottom', lineHeight: '1.2', textAlign: 'center' }}>Payment<br />Method</th>
                <th style={{ width: '105px', padding: '0.6rem 0.5rem', verticalAlign: 'bottom', lineHeight: '1.2', textAlign: 'right' }}>Total<br />amount</th>
                <th style={{ width: '105px', padding: '0.6rem 0.5rem', verticalAlign: 'bottom', lineHeight: '1.2', textAlign: 'right' }}>Total<br />paid</th>
                <th style={{ width: '115px', padding: '0.6rem 0.5rem', verticalAlign: 'bottom', lineHeight: '1.2', textAlign: 'center' }}>Added<br />By</th>
                <th style={{ width: '110px', padding: '0.6rem 0.5rem', verticalAlign: 'bottom', lineHeight: '1.2', textAlign: 'center' }}>Sell<br />note</th>
                <th style={{ width: '110px', padding: '0.6rem 0.5rem', verticalAlign: 'bottom', lineHeight: '1.2', textAlign: 'center' }}>Staff<br />note</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((r, i) => (
                <tr key={r.id || i}>
                  <td style={{ width: '150px', verticalAlign: 'middle', padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', width: '100%', alignItems: 'center' }}>
                      <button
                        onClick={() => handleViewSale(r)}
                        style={{
                          width: '100%',
                          justifyContent: 'center',
                          boxSizing: 'border-box',
                          whiteSpace: 'nowrap',
                          background: '#fff',
                          border: '1px solid #3b82f6',
                          color: '#2563eb',
                          borderRadius: '14px',
                          padding: '0.2rem 0.5rem',
                          height: '24px',
                          cursor: 'pointer',
                          fontSize: '0.74rem',
                          fontWeight: 600,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}
                      >
                        👁️ View
                      </button>

                      <button
                        onClick={() => openMapModal(r)}
                        style={{
                          width: '100%',
                          justifyContent: 'center',
                          boxSizing: 'border-box',
                          whiteSpace: 'nowrap',
                          background: r.is_mapped ? '#fff7ed' : '#fff',
                          border: r.is_mapped ? '1px solid #f97316' : '1px solid #8b5cf6',
                          color: r.is_mapped ? '#c2410c' : '#7c3aed',
                          borderRadius: '14px',
                          padding: '0.2rem 0.5rem',
                          height: '24px',
                          cursor: 'pointer',
                          fontSize: '0.74rem',
                          fontWeight: 600,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}
                      >
                        🔗 {r.is_mapped ? 'Edit Mapping' : 'Map Transaction'}
                      </button>

                      {(r.sub_type === 'sell_payment' || r.type === 'sell_payment') && (
                        <button
                          onClick={async () => {
                            if (!window.confirm('Record NSF / Bounced Check for this payment?')) return;
                            try {
                              await accountingApi.saveNsfCheck({ transaction_payment_id: r.id, note: 'Bounced Check' });
                              toast.success('NSF Check recorded successfully!');
                            } catch(e) { toast.error(e.message); }
                          }}
                          style={{ width: '100%', justifyContent: 'center', boxSizing: 'border-box', whiteSpace: 'nowrap', background: '#fee2e2', border: '1px solid #ef4444', color: '#dc2626', borderRadius: '999px', padding: '0.15rem 0.4rem', height: '22px', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center' }}
                        >
                          🚫 NSF Reversal
                        </button>
                      )}
                    </div>
                  </td>
                  <td style={{ width: '135px', fontSize: '0.85rem', whiteSpace: 'nowrap', fontWeight: 500, color: 'var(--acc-text-muted)', textAlign: 'center' }}>{r.transaction_date || r.date || '—'}</td>
                  <td style={{ width: '115px', wordBreak: 'break-all', wordWrap: 'break-word', paddingRight: '0.4rem', textAlign: 'left' }}><span style={{ fontWeight: 700, color: 'var(--acc-primary)', wordBreak: 'break-all', display: 'inline-block', lineHeight: '1.2' }}>{r.invoice_no || r.ref_no || '—'}</span></td>
                  <td style={{ width: '140px', fontWeight: 600, fontSize: '0.82rem', wordBreak: 'break-word', lineHeight: '1.25', textAlign: 'left' }}>{r.contact_name || r.customer || '—'}</td>
                  <td style={{ width: '120px', fontSize: '0.85rem', color: 'var(--acc-text-muted)', whiteSpace: 'nowrap', textAlign: 'center' }}>{r.mobile || '—'}</td>
                  <td style={{ width: '115px', fontSize: '0.85rem', color: 'var(--acc-text-muted)', whiteSpace: 'nowrap', textAlign: 'center' }}>{r.location_name || '—'}</td>
                  <td style={{ width: '110px', textAlign: 'center' }}><StatusBadge status={r.payment_status || r.paymentStatus || '—'} /></td>
                  <td style={{ width: '120px', fontWeight: 500, color: 'var(--acc-text-muted)', whiteSpace: 'nowrap', textAlign: 'center' }}>{r.payment_method || r.paymentMethod || '—'}</td>
                  <td style={{ width: '105px', fontWeight: 700, whiteSpace: 'nowrap', textAlign: 'right' }}>$ {(parseFloat(r.final_total || r.totalAmount) || 0).toFixed(2)}</td>
                  <td style={{ width: '105px', fontWeight: 700, whiteSpace: 'nowrap', textAlign: 'right', color: parseFloat(r.total_paid || r.totalPaid) >= parseFloat(r.final_total || r.totalAmount) ? 'var(--acc-success)' : 'var(--acc-warning)' }}>$ {(parseFloat(r.total_paid || r.totalPaid) || 0).toFixed(2)}</td>
                  <td style={{ width: '115px', fontSize: '0.85rem', color: 'var(--acc-text-muted)', whiteSpace: 'nowrap', textAlign: 'center' }}>{r.added_by || '—'}</td>
                  <td style={{ width: '110px', fontSize: '0.85rem', color: 'var(--acc-text-muted)', whiteSpace: 'nowrap', textAlign: 'center' }}>{r.additional_notes || '—'}</td>
                  <td style={{ width: '110px', fontSize: '0.85rem', color: 'var(--acc-text-muted)', whiteSpace: 'nowrap', textAlign: 'center' }}>{r.staff_note || '—'}</td>
                </tr>
              ))}
              {allFiltered.length === 0 && (
                <tr>
                  <td colSpan={13}>
                    <div className="acc-empty">
                      <div className="acc-empty-icon">💳</div>
                      <h4>No transactions found</h4>
                      <p>Try adjusting your search or filters to find what you're looking for.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── DataTables Pagination Controls ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.25rem', padding: '0 0.5rem 0.5rem 0.5rem', flexWrap: 'wrap', gap: '1rem', fontSize: '0.85rem', color: '#475569' }}>
          <div>
            Showing {startEntry} to {endEntry} of {allFiltered.length} entries
          </div>

          <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
            <button
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              style={{
                padding: '0.35rem 0.75rem',
                borderRadius: '4px',
                border: '1px solid #cbd5e1',
                background: currentPage <= 1 ? '#f1f5f9' : '#fff',
                color: currentPage <= 1 ? '#94a3b8' : '#1e293b',
                cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '0.85rem'
              }}
            >
              Previous
            </button>

            {getPaginationRange(currentPage, totalPages).map((p, idx) => (
              p === '...' ? (
                <span key={`ellipsis-${idx}`} style={{ padding: '0.35rem 0.5rem', color: '#94a3b8', fontSize: '0.85rem' }}>...</span>
              ) : (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  style={{
                    padding: '0.35rem 0.75rem',
                    borderRadius: '4px',
                    border: '1px solid #cbd5e1',
                    background: currentPage === p ? '#2563eb' : '#fff',
                    color: currentPage === p ? '#fff' : '#1e293b',
                    fontWeight: currentPage === p ? 700 : 500,
                    cursor: 'pointer',
                    fontSize: '0.85rem'
                  }}
                >
                  {p}
                </button>
              )
            ))}

            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              style={{
                padding: '0.35rem 0.75rem',
                borderRadius: '4px',
                border: '1px solid #cbd5e1',
                background: currentPage >= totalPages ? '#f1f5f9' : '#fff',
                color: currentPage >= totalPages ? '#94a3b8' : '#1e293b',
                cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '0.85rem'
              }}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* ── Filter Modal ── */}
      {showFilter && (
        <div
          onClick={() => setShowFilter(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: 'var(--acc-surface)', borderRadius: 'var(--acc-radius-lg)', width: '95%', maxWidth: '480px', overflow: 'hidden', boxShadow: 'var(--acc-shadow-lg)', animation: 'fadeIn 0.3s ease' }}
          >
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--acc-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--acc-bg)' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: 'var(--acc-text-main)' }}>Filter Transactions</h3>
              <button onClick={() => setShowFilter(false)} style={{ background: 'var(--acc-surface)', border: '1px solid var(--acc-border)', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--acc-text-muted)', transition: 'all 0.2s' }} onMouseEnter={e => { e.currentTarget.style.color = 'var(--acc-danger)'; e.currentTarget.style.borderColor = 'var(--acc-danger)' }} onMouseLeave={e => { e.currentTarget.style.color = 'var(--acc-text-muted)'; e.currentTarget.style.borderColor = 'var(--acc-border)' }}>✕</button>
            </div>
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div className="acc-form-group">
                <label>Payment Status</label>
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                  {['All', 'Paid', 'Due', 'Partial', 'Overdue'].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="acc-form-group">
                <label>Payment Method</label>
                <select value={filterMethod} onChange={e => setFilterMethod(e.target.value)}>
                  {['All', 'Cash', 'Card', 'Bank'].map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div style={{ padding: '1.25rem 1.5rem', borderTop: '1px solid var(--acc-border)', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', background: 'var(--acc-bg)' }}>
              <button className="acc-btn-secondary" onClick={() => { resetFilters(); setShowFilter(false); }}>
                Reset Filters
              </button>
              <button className="acc-btn-primary" onClick={() => setShowFilter(false)}>
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Map GL Modal ── */}
      {mapModal && (
        <div
          onClick={() => setMapModal(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: 'var(--acc-surface,#fff)', borderRadius: '12px', width: '95%', maxWidth: '520px', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}
          >
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#1e293b' }}>
                Map GL Account: {mapModal.row?.invoice_no || mapModal.row?.ref_no}
              </h3>
              <button onClick={() => setMapModal(null)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '0.4rem' }}>
                  Deposit To / Sales GL Account:
                </label>
                <select
                  value={mapModal.depositTo}
                  onChange={e => setMapModal(m => ({ ...m, depositTo: e.target.value }))}
                  style={{ width: '100%', padding: '0.6rem', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '0.9rem' }}
                >
                  <option value="">Select Account</option>
                  {mapModal.accounts.map(a => (
                    <option key={a.id} value={a.id}>{a.name} ({a.account_primary_type})</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '0.4rem' }}>
                  Payment / Receivable GL Account:
                </label>
                <select
                  value={mapModal.paymentAccount}
                  onChange={e => setMapModal(m => ({ ...m, paymentAccount: e.target.value }))}
                  style={{ width: '100%', padding: '0.6rem', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '0.9rem' }}
                >
                  <option value="">Select Account</option>
                  {mapModal.accounts.map(a => (
                    <option key={a.id} value={a.id}>{a.name} ({a.account_primary_type})</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '0.4rem' }}>
                  Description / Note:
                </label>
                <input
                  type="text"
                  value={mapModal.description}
                  onChange={e => setMapModal(m => ({ ...m, description: e.target.value }))}
                  placeholder="Mapping note..."
                  style={{ width: '100%', padding: '0.6rem', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '0.9rem' }}
                />
              </div>
            </div>
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', background: '#f8fafc' }}>
              <button onClick={() => setMapModal(null)} style={{ padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                Cancel
              </button>
              <button onClick={handleSaveMap} disabled={mapModal.saving} style={{ padding: '0.5rem 1.25rem', borderRadius: '6px', border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                {mapModal.saving ? 'Saving...' : 'Save Mapping'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── View Sale Modal ── */}
      {selectedSaleForView && (
        <ViewSaleModal
          sale={selectedSaleForView}
          onClose={() => setSelectedSaleForView(null)}
          onPackingSlip={(sale) => toast.info(`Packing slip for Invoice #${sale.invoice_no || sale.ref_no}`)}
          onPrintInvoice={(sale) => toast.info(`Print Invoice #${sale.invoice_no || sale.ref_no}`)}
        />
      )}

      {/* ── View Purchase Modal ── */}
      {selectedPurchaseForView && (
        <ViewPurchaseModal
          purchase={selectedPurchaseForView}
          onClose={() => setSelectedPurchaseForView(null)}
          onPrint={() => printPage()}
        />
      )}

      {/* ── View Expense Modal ── */}
      {selectedExpenseForView && (
        <div
          onClick={() => setSelectedExpenseForView(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '12px', width: '95%', maxWidth: '640px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}
          >
            {/* Header */}
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#1e293b' }}>
                Expense Details (Reference No: {selectedExpenseForView.ref_no || selectedExpenseForView.invoice_no || '—'})
              </h3>
              <button onClick={() => setSelectedExpenseForView(null)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>

            {/* Body */}
            <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.9rem' }}>
                <div><strong>Date:</strong> {selectedExpenseForView.transaction_date || selectedExpenseForView.date || '—'}</div>
                <div><strong>Reference No:</strong> {selectedExpenseForView.ref_no || selectedExpenseForView.invoice_no || '—'}</div>
                <div><strong>Location:</strong> {selectedExpenseForView.location_name || '—'}</div>
                <div><strong>Contact / Payee:</strong> {selectedExpenseForView.contact_name || '—'}</div>
                <div><strong>Payment Status:</strong> <span className={`acc-badge ${(selectedExpenseForView.payment_status || '').toLowerCase() === 'paid' ? 'acc-badge-green' : 'acc-badge-yellow'}`}>{selectedExpenseForView.payment_status || 'Paid'}</span></div>
                <div><strong>Payment Method:</strong> {selectedExpenseForView.payment_method || 'Cash'}</div>
                <div><strong>Total Amount:</strong> <span style={{ fontWeight: 700, color: '#0f172a' }}>${(parseFloat(selectedExpenseForView.final_total || selectedExpenseForView.amount) || 0).toFixed(2)}</span></div>
                <div><strong>Total Paid:</strong> <span style={{ fontWeight: 700, color: '#166534' }}>${(parseFloat(selectedExpenseForView.total_paid || selectedExpenseForView.amount) || 0).toFixed(2)}</span></div>
              </div>

              {(selectedExpenseForView.additional_notes || selectedExpenseForView.staff_note) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.875rem' }}>
                  {selectedExpenseForView.additional_notes && (
                    <div><strong>Additional Notes:</strong> {selectedExpenseForView.additional_notes}</div>
                  )}
                  {selectedExpenseForView.staff_note && (
                    <div><strong>Staff Note:</strong> {selectedExpenseForView.staff_note}</div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', background: '#f8fafc' }}>
              <button onClick={() => printPage()} style={{ padding: '0.5rem 1.25rem', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
                🖨️ Print
              </button>
              <button onClick={() => setSelectedExpenseForView(null)} style={{ padding: '0.5rem 1.5rem', borderRadius: '6px', border: 'none', background: '#334155', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Transactions;
